import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';
import Helmet from 'react-helmet';

import SignupAlbumLayout from '../../../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../../../components/global/FullScreenLoader';
import AccountAuthOptionStep from './components/AccountAuthOptionStep';
import ContactEntryStep from './components/ContactEntryStep';
import PinVerifyStep from './components/PinVerifyStep';
import useRosterQrCodeOnboarding from './hooks/useRosterQrCodeOnboarding';
import useAlbumMembershipGroupWait from './hooks/useAlbumMembershipGroupWait';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import withGoogleRecaptchaProvider from '../../../../../components/auth/hocs/withGoogleRecaptchaProvider';
import { RosterQrCodeForOnboarding } from '../../../../../graphql/queries/roster-identitiy';
import { getAsset } from '../../../../../util/assets';
import { getAccountAuthOptions, getLinkableAccount } from './helpers';
import { getErrorString } from '../../../../../util/normalize-errors';

const RosterQrCodeOnboard = () => {
  const { rosterQrCodeId } = useParams();
  const history = useHistory();
  const openAlert = useOpenAlert();
  const authenticated = useSelector(({ auth }) => auth.authenticated);

  const { data: { rosterQrCodeForOnboarding } = {}, loading: queryLoading } =
    useQuery(RosterQrCodeForOnboarding, {
      variables: { roster_qr_code_id: rosterQrCodeId },
      skip: !rosterQrCodeId,
    });

  const accounts = rosterQrCodeForOnboarding?.accounts ?? [];
  const firstName = rosterQrCodeForOnboarding?.roster_identity?.first_name;
  const albumFolderId =
    rosterQrCodeForOnboarding?.album_folder?.id ??
    rosterQrCodeForOnboarding?.album?.root_album_folder?.id;

  // Face recognition galleries can only match the identity once we have a
  // reference image on file, so onboarding has to ask for a selfie first.
  const requiresSelfie =
    (rosterQrCodeForOnboarding?.album_folder?.face_recognition_disabled ??
      rosterQrCodeForOnboarding?.album?.root_album_folder
        ?.face_recognition_disabled) === false &&
    !rosterQrCodeForOnboarding?.roster_identity?.has_matching_enabled_selfie;

  // The available authentication options. Accounts can expose multiple email
  // addresses (and/or a phone number), so there can be more options than
  // accounts.
  const authOptions = useMemo(
    () => getAccountAuthOptions(accounts),
    [accounts],
  );

  // The marketing account to link the authenticated identity to. Scenario C
  // authenticates with a contact the user provides, so the QR code's account is
  // the only thing tying the sign up to the roster identity.
  const linkableAccount = useMemo(
    () => getLinkableAccount(accounts),
    [accounts],
  );

  // The selected authentication target ({ account, method, emailAddress,
  // phoneNumber }). For scenario A (a single option) this is set automatically,
  // for scenario B (multiple options) it is set once the user picks one of the
  // obfuscated options and for scenario C once they entered a contact method.
  const [selectedAuth, setSelectedAuth] = useState();

  // Scenario A: a single authentication option is available, so we can
  // automatically pin authenticate them.
  useEffect(() => {
    if (authOptions.length === 1) {
      const [option] = authOptions;
      setSelectedAuth({
        account: option.account,
        method: option.method,
        emailAddress: option.emailAddress,
      });
    }
  }, [authOptions]);

  // Scenario B: multiple authentication options are tied to the QR code, so we
  // present the obfuscated options before requesting a pin.
  const showAuthOptions = authOptions.length > 1 && !selectedAuth;

  // Scenario C: none of the accounts of the QR code has a contact method on
  // file, so there is nothing to send a pin to until the user provides one.
  const showContactEntry =
    !!rosterQrCodeForOnboarding && !authOptions.length && !selectedAuth;

  const {
    loading,
    linkPending,
    linkCompleted,
    error,
    authMethodIsPhone,
    emailAddress,
    phoneNumber,
    requireHumanInput,
    blockLogin,
    onPinVerify,
    onPinResend,
  } = useRosterQrCodeOnboarding({
    account: selectedAuth?.account,
    method: selectedAuth?.method,
    emailAddress: selectedAuth?.emailAddress,
    phoneNumber: selectedAuth?.phoneNumber,
  });

  // The membership group backing both redirect targets is written
  // asynchronously once the marketing account is linked, so it can still be
  // missing by the time the link mutation resolves. Both targets treat a
  // missing group as "this account was never onboarded" and send the user off
  // to the wrong place, so wait for it to show up. Only linked accounts are
  // waited on: a standard account is the identity itself and already has its
  // group, so there would be nothing to wait for.
  const { waiting: waitingForMembershipGroup } = useAlbumMembershipGroupWait({
    entityId: albumFolderId,
    enabled: authenticated && !linkPending && linkCompleted,
  });

  // Linking the marketing account can outlive the authenticated state (the
  // phone verification surfaces it before the link resolves), so hold the
  // redirect until there is nothing left to link.
  useEffect(() => {
    if (
      queryLoading ||
      !authenticated ||
      linkPending ||
      waitingForMembershipGroup
    ) {
      return;
    }

    if (!albumFolderId) {
      history.push('/app');
      return;
    }

    history.push(
      requiresSelfie
        ? `/upload-selfie/${albumFolderId}`
        : `/consumer/matches/${albumFolderId}`,
    );
  }, [
    authenticated,
    linkPending,
    waitingForMembershipGroup,
    queryLoading,
    albumFolderId,
    requiresSelfie,
  ]);

  const onVerify = async code => {
    try {
      await onPinVerify(code);
    } catch (e) {
      openAlert({
        message: 'There was an error verifying your code',
        info: getErrorString(e),
      });
    }
  };

  // Once authenticated the effect above navigates away, so there is nothing
  // left to authenticate here.
  if (queryLoading || authenticated) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Verify</title>
      </Helmet>
      <div className="roster-qr-code-onboard-wrapper">
        <div className="d-flex justify-content-end align-items-center p-3">
          <div className="waldo-logo">
            <img
              src={getAsset('/powered-by-waldo-blue.svg')}
              alt="Waldo"
              style={{ height: '30px' }}
            />
          </div>
        </div>

        <SignupAlbumLayout.Wrapper
          imageDisabled
          logoSrc={null}
          withCTA={false}
          className="roster-qr-code-onboard-layout"
        >
          <div className="roster-qr-code-onboard-content-container">
            {showAuthOptions && (
              <AccountAuthOptionStep
                accounts={accounts}
                firstName={firstName}
                onSelect={(account, method, emailAddress) =>
                  setSelectedAuth({ account, method, emailAddress })
                }
              />
            )}
            {showContactEntry && (
              <ContactEntryStep
                loading={loading}
                onSubmit={contact =>
                  setSelectedAuth({ account: linkableAccount, ...contact })
                }
              />
            )}
            {!!selectedAuth && (
              <PinVerifyStep
                authMethodIsPhone={authMethodIsPhone}
                emailAddress={emailAddress}
                phoneNumber={phoneNumber}
                loading={loading}
                error={error}
                requireHumanInput={requireHumanInput}
                blockLogin={blockLogin}
                onVerify={onVerify}
                onPinResend={onPinResend}
                // Scenario A has a single option to authenticate with, so there
                // is nothing to go back to.
                onBack={
                  authOptions.length === 1
                    ? undefined
                    : () => setSelectedAuth(undefined)
                }
                backLabel={
                  authOptions.length
                    ? 'Choose another method'
                    : 'Use another contact'
                }
              />
            )}
          </div>
        </SignupAlbumLayout.Wrapper>
      </div>
    </Fragment>
  );
};

// The pin request for phone accounts is captcha protected, so the recaptcha
// provider has to be mounted for this (unauthenticated) route.
export default withGoogleRecaptchaProvider(RosterQrCodeOnboard);
